import React, { useState, useEffect, useRef } from 'react';
import { Alert, SpaceBetween, Button } from '@amzn/awsui-components-react';
import deviceStatusHandler from '../../services/deviceStatusHandler';
import { logComponentInstallationEvent } from '../../utils/logComponentInstallationEvent';
import { EventType } from '../../services/eventService';
import { compareVersions } from 'compare-versions';
import ReportComponentFailure from './reportComponentFailure';
import { ComponentType } from './componentType';

// Constants
const MIN_REQUIRED_VDUP_VERSION = '1.0.5199.0'; // https://build.amazon.com/7563626168

interface SoftwarePlatformCheckProps {
    deviceId: string;
    vin: string;
    onSuccess: (deviceInfo: any) => void;
    onReturnHome: () => void;
    componentType: ComponentType;
}

const SoftwarePlatformCheck: React.FC<SoftwarePlatformCheckProps> = ({
    deviceId,
    vin,
    onSuccess,
    onReturnHome,
    componentType,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showReportError, setShowReportError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [defaultFailureReason, setDefaultFailureReason] = useState<string | undefined>(undefined);
    const [defaultNotes, setDefaultNotes] = useState<string | undefined>(undefined);
    const isLoadingDeviceInfo = useRef(false);

    const handleReportError = () => {
        setShowReportError(true);
    };

    const handleCancelReportError = () => {
        setShowReportError(false);
    };

    const handleErrorReported = () => {
        onReturnHome();
    };

    useEffect(() => {
        // Only run once when component mounts
        if (isLoadingDeviceInfo.current) {
            return;
        }

        isLoadingDeviceInfo.current = true;
        const startTime = Date.now();

        setLoading(true);
        setError(null);

        const handleDeviceInfoResult = (success: boolean, data: any) => {
            isLoadingDeviceInfo.current = false;
            setLoading(false);

            if (!success || !data) {
                setError('Failed to load device information. Please check your connection and try again.');

                logComponentInstallationEvent({
                    deviceId,
                    vin,
                    componentType: componentType.value,
                    componentId: 'unknown',
                    eventType: EventType.FAILURE,
                    startTime,
                    error: 'Failed to load device information',
                });
                return;
            }

            // Check VDUP version requirement
            if (data.deploymentVersions && data.deploymentVersions.vdupVersion) {
                const vdupVersion = data.deploymentVersions.vdupVersion;

                if (compareVersions(vdupVersion, MIN_REQUIRED_VDUP_VERSION) < 0) {
                    const errorMessage =
                        `Device software is not compatible with this component installation. ` +
                        `Please stop the installation and report this device to Amazon.`;

                    setError(errorMessage);

                    // Set default values for VDUP version failure
                    setDefaultFailureReason('fleet_edge_outdated');
                    setDefaultNotes('Device software is not compatible with this component installation');

                    logComponentInstallationEvent({
                        deviceId,
                        vin,
                        componentType: componentType.value,
                        componentId: 'unknown',
                        eventType: EventType.FAILED_VALIDATION,
                        startTime,
                        error: `VDUP version ${vdupVersion} below minimum required ${MIN_REQUIRED_VDUP_VERSION}`,
                    });
                    return;
                }
            }

            setShowSuccess(true);

            setTimeout(() => {
                onSuccess(data);
            }, 3000); // Show success message for 3 seconds
        };

        // Set a timeout to handle cases where the API call hangs
        const timeoutId = setTimeout(() => {
            isLoadingDeviceInfo.current = false;
            setLoading(false);
            setError('Request timed out. Please check your connection and try again.');
        }, 30000); // 30 second timeout

        try {
            // Call the device status handler to get device information
            deviceStatusHandler(
                deviceId,
                vin,
                false,
                (success: boolean, data: any) => {
                    clearTimeout(timeoutId);
                    handleDeviceInfoResult(success, data);
                },
                true,
            );
        } catch (error) {
            clearTimeout(timeoutId);
            isLoadingDeviceInfo.current = false;
            setLoading(false);
            setError('An error occurred while loading device information. Please try again.');
        }
    }, []); // Only run once on component mount

    // Show the report error component if user clicked report error
    if (showReportError) {
        return (
            <ReportComponentFailure
                deviceId={deviceId}
                vin={vin}
                componentType={componentType}
                serialNumber="unknown"
                onFailureReported={handleErrorReported}
                onCancel={handleCancelReportError}
                defaultFailureReason={defaultFailureReason}
                defaultNotes={defaultNotes}
            />
        );
    }

    if (loading) {
        return (
            <Alert type="info" header="Checking Software Platform Compatibility">
                Verifying device software platform compatibility... This process might take a few seconds. Please keep
                this page open and wait for the verification to complete.
            </Alert>
        );
    }

    if (showSuccess) {
        return (
            <Alert type="success" header="Software Platform Check Passed">
                The software platform satisfies the requirements for installation. Proceeding to the next step...
            </Alert>
        );
    }

    if (error) {
        return (
            <SpaceBetween size="s">
                <Alert type="error" header="Software Platform Check Failed">
                    {error}
                </Alert>
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="normal" onClick={handleReportError}>
                        Report Error to Amazon
                    </Button>
                    <Button variant="primary" onClick={onReturnHome}>
                        Return to Home
                    </Button>
                </SpaceBetween>
            </SpaceBetween>
        );
    }

    // This should not be reached as the component will call onSuccess when validation passes
    return null;
};

export default SoftwarePlatformCheck;
